<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6">
            <div class="bg-white shadow-md rounded-lg p-6 border border-gray-200 mx-auto">
                <div class="flex justify-between items-center mb-6 border-b pb-4">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-800">Commit in Beta</h1>
                        <p class="text-sm text-gray-500">
                            Confronto tra <span class="font-mono bg-gray-100 px-1 rounded">{{ targetBranch }}</span> e <span class="font-mono bg-gray-100 px-1 rounded">{{ baseBranch }}</span>
                        </p>
                    </div>
                    <div class="flex gap-2">
                        <PrimaryButton
                            @click="refreshCommits"
                            :disabled="loading"
                        >
                            <v-icon v-if="loading" name="hi-refresh" class="mr-2 animate-spin" />
                            <v-icon v-else name="hi-refresh" class="mr-2" />
                            Aggiorna
                        </PrimaryButton>
                    </div>
                </div>

                <PageNormalTable
                    :headers="headers"
                    :items="commits"
                    :loading="loading"
                >
                    <template v-slot:item.commitId="{ item }">
                        <code class="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                            {{ item.commitId }}
                        </code>
                    </template>

                    <template v-slot:item.author_date="{ item }">
                        <span>{{ formatDate(item.author_date) }}</span>
                    </template>

                    <template v-slot:item.actions="{ item }">
                        <button
                            class="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50 transition-colors"
                            @click="showChanges(item.commitId)"
                            title="Mostra file modificati"
                        >
                            <v-icon name="bi-info-circle" scale="1.2" />
                        </button>
                    </template>

                    <template v-slot:item.comment="{ item }">
                        <div class="max-w-xl truncate" :title="item.comment">
                            {{ item.comment }}
                        </div>
                    </template>
                </PageNormalTable>
            </div>
        </div>

        <!-- Modal per i file modificati -->
        <DialogModal :show="dialog.show" @close="dialog.show = false">
            <template #title>
                Dettagli aggiuntivi commit
            </template>

            <template #content>
                <div v-if="dialog.loading" class="flex justify-center py-10">
                    <v-icon name="hi-refresh" scale="2" class="animate-spin text-blue-600" />
                </div>
                <div v-else>
                    <div v-if="dialog.commit" class="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-[30vh] overflow-y-auto">
                        <h4 class="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider">Commento completo</h4>
                        <p class="text-gray-800 whitespace-pre-wrap">{{ dialog.commit.comment }}</p>
                    </div>

                    <div v-if="dialog.changes.length > 0" class="max-h-[50vh] overflow-y-auto">
                        <table class="min-w-full divide-y divide-gray-200 border">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr v-for="(change, index) in dialog.changes" :key="index" class="hover:bg-gray-50">
                                    <td class="px-4 py-2 whitespace-nowrap">
                                        <v-icon :name="getChangeIcon(change.changeType)" :class="getChangeColorClass(change.changeType)" scale="1.1" />
                                    </td>
                                    <td class="px-4 py-2 text-sm font-mono text-gray-800 break-all">
                                        {{ change.item.path }}
                                    </td>
                                    <td class="px-4 py-2 whitespace-nowrap text-xs text-gray-500">
                                        {{ translateChangeType(change.changeType) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="text-center py-10 text-gray-500">
                        Nessun file modificato trovato o errore durante il caricamento.
                    </div>
                </div>
            </template>

            <template #footer>
                <SecondaryButton @click="dialog.show = false">
                    Chiudi
                </SecondaryButton>
            </template>
        </DialogModal>
    </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageNormalTable from '@/Components/PageNormalTable.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import DialogModal from '@/Components/DialogModal.vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';

export default {
    components: {
        BreadCrumbs,
        PageNormalTable,
        PrimaryButton,
        SecondaryButton,
        DialogModal
    },
    props: {
        commits: {
            type: Array,
            default: () => []
        },
        moduleSlug: String,
        baseBranch: String,
        targetBranch: String
    },
    data() {
        return {
            loading: false,
            headers: [
                { title: 'ID Commit', align: 'start', key: 'commitId', value: 'commitId', filterable: true },
                { title: 'Descrizione', align: 'start', key: 'comment', value: 'comment', filterable: true },
                { title: 'Autore', align: 'start', key: 'author_name', value: 'author_name', filterable: true },
                { title: 'Data', align: 'start', key: 'author_date', value: 'author_date', filterable: true },
                { title: 'Info', align: 'center', key: 'actions', value: 'actions', sortable: false },
            ],
            dialog: {
                show: false,
                loading: false,
                changes: [],
                commit: null
            }
        };
    },
    computed: {
        navigation() {
            return [
                {
                    name: 'Dashboard',
                    href: route('dashboard'),
                },
                {
                    name: 'Commit in Beta',
                    href: route('modules.home', 'commit-in-beta'),
                }
            ];
        }
    },
    methods: {
        refreshCommits() {
            this.loading = true;
            router.reload({
                onFinish: () => {
                    this.loading = false;
                }
            });
        },
        async showChanges(commitId) {
            this.dialog.show = true;
            this.dialog.loading = true;
            this.dialog.changes = [];
            this.dialog.commit = null;

            try {
                const response = await axios.post(route('modules.commit-in-beta.changes'), {
                    commitId: commitId
                });
                if (response.data.success) {
                    this.dialog.changes = response.data.changes;
                    this.dialog.commit = response.data.commit;
                }
            } catch (error) {
                console.error('Errore durante il caricamento dei file modificati', error);
            } finally {
                this.dialog.loading = false;
            }
        },
        getChangeIcon(type) {
            switch (type) {
                case 'add': return 'co-plus';
                case 'edit': return 'bi-pencil';
                case 'delete': return 'fa-trash';
                case 'rename': return 'hi-switch-horizontal';
                default: return 'bi-file-earmark-bar-graph';
            }
        },
        getChangeColorClass(type) {
            switch (type) {
                case 'add': return 'text-green-600';
                case 'edit': return 'text-blue-600';
                case 'delete': return 'text-red-600';
                case 'rename': return 'text-orange-600';
                default: return 'text-gray-600';
            }
        },
        translateChangeType(type) {
            const types = {
                'add': 'Aggiunto',
                'edit': 'Modificato',
                'delete': 'Eliminato',
                'rename': 'Rinominato'
            };
            return types[type] || type;
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    }
};
</script>
